import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { AnimatePresence, MotionConfig } from 'framer-motion';
import { Button } from '@/components/primitives/Button';
import { Spinner } from '@/components/primitives/Spinner';
import { Chips } from '@/components/Chips';
import { Glyph } from '@/components/Glyph';
import { Icon } from '@/components/Icon';
import { PageTitle } from '@/components/PageTitle';
import { SidePanel } from '@/components/SidePanel';
import { ListRow, SettingsGroup, SettingsLoading, SettingsNote, SettingsState, } from '@/components/settings/Settings';
import { apiErrorText } from '@/api/opengater/errors';
import { ApiError } from '@/api/client';
import { useOgUserCard, useOgUserSearch } from '@/api/opengater/useUserCard';
import { useDebouncedValue } from '@/lib/useDebouncedValue';
import u from './OpengaterUsers.module.css';
import { useSelectedUserId } from './useSelectedUserId';
import { CardError, CardSkeleton, UserCard } from './UserCard';
import { OperationCard } from './userActions/OperationCard';
import { clearTrackedOperation, readTrackedOperation } from './userActions/operationStorage';
import { ActionNotice } from './userActions/UserActionUi';
import { currencySymbol, initials, money } from './userCardFormat';
import { accountStatusView } from './userActions/accountStatus';
const INT_RE = /^\d+$/;
/** Balances are stored in rubles, whatever currency the user sees. */
const RUB = currencySymbol('RUB');
const EMPTY_FILTERS = { status: 'all', expFrom: '', expTo: '', hasBalance: false };
function accountState(status) {
    if (status == null)
        return 'none';
    if (status === 'active')
        return 'active';
    if (status === 'awaiting_cutoff')
        return 'expiring';
    return 'expired';
}
function matchFilters(it, f) {
    const st = accountState(it.account_status);
    if (f.status === 'active' && st !== 'active' && st !== 'expiring')
        return false;
    if (f.status === 'disabled' && st !== 'expired')
        return false;
    if (f.status === 'none' && st !== 'none')
        return false;
    if (f.hasBalance && !(it.balance > 0))
        return false;
    if (f.expFrom && (!it.expire || it.expire < f.expFrom))
        return false;
    if (f.expTo && (!it.expire || it.expire > f.expTo))
        return false;
    return true;
}
function sameFilters(a, b) {
    return (a.status === b.status &&
        a.expFrom === b.expFrom &&
        a.expTo === b.expTo &&
        a.hasBalance === b.hasBalance);
}
function userLabel(it) {
    return it.full_name || it.username || it.email || `#${it.id}`;
}
/** Secondary identifier under the name — whatever the name isn't already. */
function secondary(it) {
    const name = userLabel(it);
    if (it.email && it.email !== name)
        return it.email;
    if (it.username && it.username !== name)
        return `@${it.username}`;
    return '';
}
export function OpengaterUsers() {
    const [query, setQuery] = useState('');
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const { id: loadedId, open: openUser, close: closeUser } = useSelectedUserId();
    const debounced = useDebouncedValue(query.trim(), 300);
    const search = useOgUserSearch(debounced);
    const card = useOgUserCard(loadedId);
    const trackedOperation = loadedId != null && card.error instanceof ApiError && card.error.status === 404
        ? readTrackedOperation(loadedId)
        : null;
    const loaded = (search.data?.pages ?? []).flatMap((p) => p.items);
    const total = search.data?.pages[0]?.total ?? 0;
    const rows = loaded.filter((it) => matchFilters(it, filters));
    const filtered = !sameFilters(filters, EMPTY_FILTERS);
    const onSubmit = (e) => {
        e.preventDefault();
        const n = INT_RE.test(query.trim()) ? Number(query.trim()) : null;
        if (n && n > 0)
            openUser(n);
        else if (rows[0])
            openUser(rows[0].id);
    };
    return (_jsxs(_Fragment, { children: [_jsx(PageTitle, { title: "\u041F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u0435\u043B\u0438", sub: "\u041F\u043E\u0438\u0441\u043A \u0438 \u0444\u0438\u043B\u044C\u0442\u0440\u044B \u0434\u043B\u044F \u043F\u043E\u0434\u0434\u0435\u0440\u0436\u043A\u0438" }), _jsxs("form", { onSubmit: onSubmit, className: u.search, children: [_jsxs("span", { className: u.searchField, children: [_jsx(Icon, { name: "search", size: 15, className: u.searchIcon }), _jsx("input", { value: query, placeholder: "ID, email, \u0438\u043C\u044F, username, chat_id, \u0441\u0441\u044B\u043B\u043A\u0430\u2026", "aria-label": "\u041F\u043E\u0438\u0441\u043A \u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u0435\u043B\u0435\u0439", onChange: (e) => setQuery(e.target.value) })] }), _jsx(Button, { type: "submit", children: "\u041D\u0430\u0439\u0442\u0438" })] }), _jsxs("p", { className: u.searchHint, children: ["\u0426\u0438\u0444\u0440\u044B \u2014 ID \u0438\u043B\u0438 chat_id, ", _jsx("span", { className: u.mono, children: "@" }), " \u2014 email, UUID \u2014 GlobalAuth,", ' ', _jsx("span", { className: u.mono, children: "/" }), " \u2014 \u0441\u0441\u044B\u043B\u043A\u0430, \u043E\u0441\u0442\u0430\u043B\u044C\u043D\u043E\u0435 \u2014 \u0438\u043C\u044F, username \u0438\u043B\u0438 email. Enter \u043E\u0442\u043A\u0440\u044B\u0432\u0430\u0435\u0442 \u043F\u0435\u0440\u0432\u043E\u0433\u043E \u043D\u0430\u0439\u0434\u0435\u043D\u043D\u043E\u0433\u043E."] }), _jsx(FilterBar, { filters: filters, dirty: filtered, onChange: setFilters }), !!search.error && (_jsx(SettingsNote, { tone: "error", children: apiErrorText(search.error, 'Не удалось загрузить список.') })), search.isLoading ? (_jsx(SettingsLoading, {})) : rows.length === 0 ? (!search.error && (_jsx(SettingsState, { children: loaded.length === 0 ? 'Никого не нашли' : 'Под фильтры никто не подходит' }))) : (_jsx(SettingsGroup, { footer: filtered ? 'Фильтры применяются к загруженным строкам.' : undefined, children: rows.map((it) => (_jsx(ListRow, { leading: _jsx(UserGlyph, { it: it, active: it.id === loadedId }), title: userLabel(it), sub: [
                        `#${it.id}`,
                        secondary(it) || null,
                        it.balance !== 0 ? `баланс ${money(it.balance, RUB)}` : null,
                    ]
                        .filter(Boolean)
                        .join(' · '), value: _jsx(SubText, { status: it.account_status }), active: it.id === loadedId, onOpen: () => openUser(it.id) }, it.id))) })), loaded.length > 0 && (_jsxs("div", { className: u.foot, children: [_jsxs("span", { children: [filtered
                                ? `${rows.length} из ${loaded.length} загруженных`
                                : `Загружено ${loaded.length}`, total ? ` · всего ${total}` : ''] }), search.hasNextPage && (_jsx(Button, { variant: "ghost", onClick: () => void search.fetchNextPage(), disabled: search.isFetchingNextPage, children: search.isFetchingNextPage ? _jsx(Spinner, { size: 13 }) : 'Показать ещё' }))] })), _jsx(MotionConfig, { reducedMotion: "user", children: _jsx(AnimatePresence, { children: loadedId != null && (_jsxs(SidePanel, { title: `Пользователь #${loadedId}`, onClose: closeUser, children: [card.isLoading && _jsx(CardSkeleton, {}), !!card.error &&
                                !card.isLoading &&
                                (trackedOperation ? (_jsx(StoredOperationRecovery, { userId: loadedId, operationId: trackedOperation })) : (_jsx(CardError, { text: apiErrorText(card.error, `Пользователь #${loadedId} не найден.`) }))), card.data && (_jsx(UserCard, { card: card.data, onUserMissing: closeUser }, card.data.id))] }, "user-card")) }) })] }));
}
export function StoredOperationRecovery({ userId, operationId, }) {
    const [terminal, setTerminal] = useState(false);
    const [dismissed, setDismissed] = useState(false);
    if (dismissed) {
        return _jsx(CardError, { text: `Пользователь #${userId} удалён или недоступен.` });
    }
    return (_jsxs("div", { style: { maxWidth: 744, margin: '58px auto', padding: '0 28px' }, children: [_jsx(ActionNotice, { tone: "warning", children: "\u041F\u0440\u043E\u0432\u0435\u0440\u044F\u0435\u043C \u0437\u0430\u0432\u0435\u0440\u0448\u0435\u043D\u0438\u0435 \u0443\u0434\u0430\u043B\u0435\u043D\u0438\u044F" }), _jsx(OperationCard, { operationId: operationId, onSettled: () => setTerminal(true) }), terminal ? (_jsx("div", { style: { display: 'flex', justifyContent: 'flex-end', marginTop: 12 }, children: _jsx(Button, { variant: "ghost", onClick: () => {
                        clearTrackedOperation(userId);
                        setDismissed(true);
                    }, children: "\u0421\u043A\u0440\u044B\u0442\u044C \u0440\u0435\u0437\u0443\u043B\u044C\u0442\u0430\u0442" }) })) : null] }));
}
/** Initials on a glyph; a plain person when the user has no name, username or email. */
function UserGlyph({ it, active }) {
    const named = !!(it.full_name || it.username || it.email);
    return named ? (_jsx(Glyph, { text: initials(userLabel(it)), size: "md", active: active })) : (_jsx(Glyph, { icon: "user", size: "md", active: active }));
}
function SubText({ status }) {
    const state = accountState(status);
    const { label, description, raw } = accountStatusView(status);
    return (_jsx("span", { className: u.sub, "data-state": state, title: raw ? `${description}: ${raw}` : description, children: label }));
}
const STATUS_OPTIONS = [
    ['all', 'Все'],
    ['active', 'Активные'],
    ['disabled', 'Отключённые'],
    ['none', 'Без аккаунта'],
];
const BALANCE_OPTIONS = [['on', 'С балансом']];
function FilterBar({ filters, dirty, onChange, }) {
    const set = (k, v) => onChange({ ...filters, [k]: v });
    return (_jsxs("div", { className: u.filters, children: [_jsx(Chips, { id: "users-status", label: "\u0421\u0442\u0430\u0442\u0443\u0441", value: filters.status, options: STATUS_OPTIONS, onChange: (v) => set('status', v) }), _jsx(Chips, { id: "users-balance", label: "\u0411\u0430\u043B\u0430\u043D\u0441", value: filters.hasBalance ? 'on' : '', options: BALANCE_OPTIONS, onChange: () => set('hasBalance', !filters.hasBalance) }), _jsxs("span", { className: u.dates, children: [_jsx("span", { className: u.datesLbl, children: "\u041F\u043E\u0434\u043F\u0438\u0441\u043A\u0430 \u0434\u043E" }), _jsx("input", { type: "date", className: u.date, value: filters.expFrom, "aria-label": "\u041F\u043E\u0434\u043F\u0438\u0441\u043A\u0430 \u0434\u043E, \u0441", onChange: (e) => set('expFrom', e.target.value) }), _jsx("span", { className: u.dash, children: "\u2013" }), _jsx("input", { type: "date", className: u.date, value: filters.expTo, "aria-label": "\u041F\u043E\u0434\u043F\u0438\u0441\u043A\u0430 \u0434\u043E, \u043F\u043E", onChange: (e) => set('expTo', e.target.value) })] }), dirty && (_jsx("button", { type: "button", className: u.reset, onClick: () => onChange(EMPTY_FILTERS), children: "\u0421\u0431\u0440\u043E\u0441\u0438\u0442\u044C" }))] }));
}
